import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { Page } from "@/app/shared/components/page";
import { PageHeader } from "@groot/shell/components/layout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import {
  useNoteByMonth,
  useUpsertNote,
  useRunningNote,
  useUpsertRunningNote,
} from "@/pages/notes/hooks/useNotes";
import { markdown } from "@codemirror/lang-markdown";
import { EditorView } from "@codemirror/view";
import CodeMirror from "@uiw/react-codemirror";
import dayjs from "dayjs";
import { Check, Loader2, Pin } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

// Default slug for the running note
const RUNNING_NOTE_SLUG = "general";

// Generate month options for the past 2 years and future 1 year
function generateMonthOptions() {
  const options: { value: string; label: string }[] = [];
  const now = dayjs();

  // Generate months from 2 years ago to 1 year in the future
  for (let i = -24; i <= 12; i++) {
    const date = now.add(i, "month");
    const month = date.format("YYYY-MM");
    const label = date.format("MMMM YYYY");
    options.push({ value: month, label });
  }

  return options.reverse(); // Most recent first
}

// Format month string to display string
function formatMonthDisplay(month: string): string {
  return dayjs(`${month}-01`).format("MMMM YYYY");
}

// Get current month in YYYY-MM format
function getCurrentMonth(): string {
  return dayjs().format("YYYY-MM");
}

export default function Notes() {
  const currentMonth = getCurrentMonth();

  // Running note state
  const [runningContent, setRunningContent] = useState<string>("");
  const [isRunningSaving, setIsRunningSaving] = useState(false);
  const [runningLastSaved, setRunningLastSaved] = useState<Date | null>(null);

  // Monthly note state
  const [selectedMonth, setSelectedMonth] = useState<string>(currentMonth);
  const [monthlyContent, setMonthlyContent] = useState<string>("");
  const [isMonthySaving, setIsMonthlySaving] = useState(false);
  const [monthlyLastSaved, setMonthlyLastSaved] = useState<Date | null>(null);

  // Running note hooks
  const { data: runningNote, isLoading: isRunningLoading } = useRunningNote(RUNNING_NOTE_SLUG);
  const upsertRunningNote = useUpsertRunningNote();

  // Monthly note hooks
  const { data: monthlyNote, isLoading: isMonthlyLoading } = useNoteByMonth(selectedMonth);
  const upsertNote = useUpsertNote();

  // Load running note content when data changes
  useEffect(() => {
    if (runningNote) {
      setRunningContent(runningNote.content || "");
      setRunningLastSaved(new Date(runningNote.updatedAt));
    } else {
      setRunningContent("");
      setRunningLastSaved(null);
    }
  }, [runningNote]);

  // Load monthly note content when note data changes
  useEffect(() => {
    if (monthlyNote) {
      setMonthlyContent(monthlyNote.content || "");
      setMonthlyLastSaved(new Date(monthlyNote.updatedAt));
    } else {
      setMonthlyContent("");
      setMonthlyLastSaved(null);
    }
  }, [monthlyNote]);

  // Auto-save for running note
  // biome-ignore lint/correctness/useExhaustiveDependencies: upsertRunningNote is stable
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (runningContent !== (runningNote?.content || "")) {
        setIsRunningSaving(true);
        upsertRunningNote.mutate(
          { slug: RUNNING_NOTE_SLUG, content: runningContent },
          {
            onSuccess: () => {
              setRunningLastSaved(new Date());
              setIsRunningSaving(false);
            },
            onError: () => {
              setIsRunningSaving(false);
            },
          },
        );
      }
    }, 1500);

    return () => clearTimeout(timeoutId);
  }, [runningContent, runningNote?.content]);

  // Auto-save for monthly note
  // biome-ignore lint/correctness/useExhaustiveDependencies: upsertNote is stable
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (monthlyContent !== (monthlyNote?.content || "")) {
        setIsMonthlySaving(true);
        upsertNote.mutate(
          { month: selectedMonth, content: monthlyContent },
          {
            onSuccess: () => {
              setMonthlyLastSaved(new Date());
              setIsMonthlySaving(false);
            },
            onError: () => {
              setIsMonthlySaving(false);
            },
          },
        );
      }
    }, 1500);

    return () => clearTimeout(timeoutId);
  }, [monthlyContent, selectedMonth, monthlyNote?.content]);

  // Handle month change
  const handleMonthChange = useCallback((value: string) => {
    setSelectedMonth(value);
  }, []);

  // Handle content changes
  const handleRunningContentChange = useCallback((value: string) => {
    setRunningContent(value);
  }, []);

  const handleMonthlyContentChange = useCallback((value: string) => {
    setMonthlyContent(value);
  }, []);

  const monthOptions = generateMonthOptions();

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title="Notes"
        description="Your personal finance scratchpad. Write notes, goals, and reflections in markdown."
        className="mb-8"
      />

      {/* Notes Grid - Side by side on desktop, stacked on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Running Notes Section */}
        <div className="border border-border rounded-lg overflow-hidden flex flex-col">
          <div className="border-b border-border bg-muted px-6 py-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Pin className="size-4 text-warning" />
                <h2 className="text-base font-medium text-foreground">Running Notes</h2>
              </div>
              <p className="text-xs text-muted-foreground">
                Persistent notes that aren't tied to any specific month. Great for ongoing goals,
                reminders, or quick thoughts.
              </p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {isRunningSaving && (
                  <>
                    <Loader2 className="size-3 animate-spin" />
                    <span>Saving...</span>
                  </>
                )}
                {!isRunningSaving && runningLastSaved && (
                  <>
                    <Check className="size-3 text-success" />
                    <span>Last saved {dayjs(runningLastSaved).fromNow()}</span>
                  </>
                )}
                {!isRunningSaving && !runningLastSaved && <span>No changes</span>}
              </div>
            </div>
          </div>

          {isRunningLoading ? (
            <LoadingSpinner size="lg" className="py-12" />
          ) : (
            <div className="notes-editor flex-1">
              <CodeMirror
                value={runningContent}
                height="calc(100vh - 400px)"
                minHeight="400px"
                extensions={[markdown(), EditorView.lineWrapping]}
                onChange={handleRunningContentChange}
                placeholder={`# Running Notes

##OngoingGoals
-

##Reminders
-

##QuickThoughts
-`}
                basicSetup={{
                  lineNumbers: true,
                  highlightActiveLineGutter: true,
                  highlightSpecialChars: true,
                  foldGutter: true,
                  drawSelection: true,
                  dropCursor: true,
                  allowMultipleSelections: true,
                  indentOnInput: true,
                  bracketMatching: true,
                  closeBrackets: true,
                  autocompletion: true,
                  rectangularSelection: true,
                  crosshairCursor: true,
                  highlightActiveLine: true,
                  highlightSelectionMatches: true,
                  closeBracketsKeymap: true,
                  searchKeymap: true,
                  foldKeymap: true,
                  completionKeymap: true,
                  lintKeymap: true,
                }}
                className="text-sm sm:text-base"
              />
            </div>
          )}
        </div>

        {/* Monthly Notes Section */}
        <div className="border border-border rounded-lg overflow-hidden flex flex-col">
          <div className="border-b border-border bg-muted px-6 py-4">
            <div className="flex flex-col gap-2">
              <h2 className="text-base font-medium text-foreground">
                Monthly Notes - {formatMonthDisplay(selectedMonth)}
              </h2>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {isMonthySaving && (
                  <>
                    <Loader2 className="size-3 animate-spin" />
                    <span>Saving...</span>
                  </>
                )}
                {!isMonthySaving && monthlyLastSaved && (
                  <>
                    <Check className="size-3 text-success" />
                    <span>Last saved {dayjs(monthlyLastSaved).fromNow()}</span>
                  </>
                )}
                {!isMonthySaving && !monthlyLastSaved && <span>No changes</span>}
              </div>
              <div className="w-full sm:w-64">
                <Select value={selectedMonth} onValueChange={handleMonthChange}>
                  <SelectTrigger className="w-full border-input">
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {isMonthlyLoading ? (
            <LoadingSpinner size="lg" className="py-20" />
          ) : (
            <div className="notes-editor flex-1">
              <CodeMirror
                value={monthlyContent}
                height="calc(100vh - 400px)"
                minHeight="400px"
                extensions={[markdown(), EditorView.lineWrapping]}
                onChange={handleMonthlyContentChange}
                placeholder={`# ${formatMonthDisplay(selectedMonth)} Finance Notes

##Goals
-

##Income
-

##Expenses
-

##Savings
-

##Notes
-`}
                basicSetup={{
                  lineNumbers: true,
                  highlightActiveLineGutter: true,
                  highlightSpecialChars: true,
                  foldGutter: true,
                  drawSelection: true,
                  dropCursor: true,
                  allowMultipleSelections: true,
                  indentOnInput: true,
                  bracketMatching: true,
                  closeBrackets: true,
                  autocompletion: true,
                  rectangularSelection: true,
                  crosshairCursor: true,
                  highlightActiveLine: true,
                  highlightSelectionMatches: true,
                  closeBracketsKeymap: true,
                  searchKeymap: true,
                  foldKeymap: true,
                  completionKeymap: true,
                  lintKeymap: true,
                }}
                className="text-sm sm:text-base"
              />
            </div>
          )}
        </div>
      </div>

      {/* Markdown Tips */}
      <div className="p-4 bg-muted rounded-lg border border-border">
        <h3 className="text-sm font-medium text-foreground mb-2">Markdown Tips:</h3>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li>
            • Use <code className="bg-accent px-1 rounded"># Heading</code> for titles
          </li>
          <li>
            • Use <code className="bg-accent px-1 rounded">**bold**</code> for emphasis
          </li>
          <li>
            • Use <code className="bg-accent px-1 rounded">- item</code> for bullet lists
          </li>
          <li>
            • Use <code className="bg-accent px-1 rounded">[ ] task</code> for checkboxes
          </li>
          <li>• Your notes are automatically saved as you type</li>
        </ul>
      </div>
    </Page>
  );
}
